/**
 * Data Source Registry
 * 
 * Central registry for all data source integrations.
 * Provides configuration, validation, and health monitoring for each source.
 */

import { getSAMApiKey, resolveSamGovIntegrationState } from '@tracekit/core';
import { DataSourceConfig, Opportunity, Award } from './dataSourceIntegrations';

// Re-export for convenience
export type { DataSourceConfig, Opportunity, Award };
import {
  fetchCanadaGrants,
  fetchCanadaTenders,
  fetchSAMGov,
  fetchWorldBank,
  fetchBCBid,
  fetchBidsTender,
  fetchBonfire,
  fetchMerx,
  fetchSAPAriba,
  fetchJaggaer,
  fetchManitoba,
  fetchSaskatchewan,
  fetchNrc,
  fetchToronto,
  fetchUngm,
  fetchAdb,
  fetchAfdb,
  fetchCityPortals,
  fetchVancouverCityPortal,
  fetchVictoriaCityPortal,
  fetchMetroVancouverCityPortal,
} from './dataSourceIntegrations';
import { getCredentialFields, requiresCredentials } from '../utils/credentialFields';
import {
  fetchUSAwards,
  fetchDepartmentBudgets,
  fetchExecutiveOrders,
  fetchFederalRegisterDocuments,
  fetchRSSFeeds,
} from './usGovernmentIntegrations';

// ============================================================================
// Type Definitions
// ============================================================================

export type DataSourceType =
  | 'canada_grants'
  | 'canada_tenders'
  | 'samgov'
  | 'worldbank'
  | 'usa_awards'           // USASpending Awards
  | 'usa_budgets'          // Department Budgets
  | 'federal_register'     // Federal Register Documents
  | 'executive_orders'     // Executive Orders
  | 'rss_feeds'            // RSS News Feeds
  | 'bc_bid'               // BC Bid (British Columbia Government Procurement)
  | 'bids_tender'          // Bids&Tender
  | 'bonfire'              // Bonfire
  | 'gbof'                 // CCC Global Bid Opportunity Finder
  | 'merx'                 // Merx
  | 'sap_ariba'            // SAP Ariba
  | 'jaggaer'              // Ontario Provincial Portal (Jaggaer ESOP)
  | 'manitoba'             // Manitoba Provincial Portal (MERX)
  | 'saskatchewan'         // Saskatchewan Provincial Portal (SaskTenders)
  | 'nrc'                  // Natural Resources Canada funding
  | 'toronto'              // City of Toronto Bids Portal
  | 'ungm'                 // UN Global Marketplace
  | 'adb'                  // Asian Development Bank
  | 'afdb'                 // African Development Bank
  | 'city_portals'         // City Portals (RSS feeds)
  | 'vancouver_city_portal' // Vancouver City Portal (Jaggaer)
  | 'victoria_city_portal'  // Victoria City Portal (Bonfire)
  | 'metrovancouver_city_portal' // Metro Vancouver City Portal (BidsAndTenders)
  // India country portals (rfp/data_sources/india_country_portals)
  | 'tender_247'
  | 'tendertiger_india'
  | 'eprocure'
  | 'gem'
  | 'hp_tenders'
  | 'tender_odisha'
  | 'uk_tenders'
  // Foreign country portals (TenderTiger)
  | 'tendertiger_brazil'
  | 'tendertiger_canada'
  | 'tendertiger_ethiopia'
  | 'tendertiger_indonesia'
  | 'tendertiger_kenya'
  | 'tendertiger_mexico'
  | 'tendertiger_peru'
  | 'tendertiger_south_africa'
  | 'tendertiger_tanzania'
  | 'tendertiger_usa';

export interface DataSourceMetadata {
  id: DataSourceType;
  name: string;
  description: string;
  api_url: string;
  documentation_url: string;
  requires_api_key: boolean;
  rate_limits: {
    public?: string;
    authenticated?: string;
  };
  supported_features: string[];
  status: 'active' | 'beta' | 'deprecated';
}

export interface DataSourceRunner {
  fetch: (config: DataSourceConfig, projectId: string) => Promise<{ opportunities: Opportunity[]; awards: Award[] }>;
  validate: (config: DataSourceConfig) => ValidationResult;
  test: (config: DataSourceConfig) => Promise<HealthCheckResult>;
}

export interface ValidationResult {
  valid: boolean;
  errors: string[];
  warnings: string[];
}

export interface HealthCheckResult {
  healthy: boolean;
  status: 'ok' | 'degraded' | 'error';
  message: string;
  response_time_ms?: number;
  last_checked: string;
}

export interface DataSourceRegistryEntry {
  metadata: DataSourceMetadata;
  runner: DataSourceRunner;
  defaultConfig: Partial<DataSourceConfig>;
}

// ============================================================================
// Data Source Metadata
// ============================================================================

const DATA_SOURCE_METADATA: Record<DataSourceType, DataSourceMetadata> = {
  canada_grants: {
    id: 'canada_grants',
    name: 'Canada Grants',
    description: 'Government of Canada Grants and Contributions',
    api_url: 'https://open.canada.ca/data/api/3/action/package_search',
    documentation_url: 'https://open.canada.ca/en/open-data',
    requires_api_key: false,
    rate_limits: {
      public: '100 requests/hour',
      authenticated: '1000 requests/hour (with API key)',
    },
    supported_features: ['keyword_search', 'date_filtering', 'organization_filtering'],
    status: 'active',
  },
  canada_tenders: {
    id: 'canada_tenders',
    name: 'Canada Tenders',
    description: 'Buyandsell.gc.ca Procurement Opportunities',
    api_url: 'https://buyandsell.gc.ca/procurement-data',
    documentation_url: 'https://buyandsell.gc.ca/procurement-data',
    requires_api_key: false,
    rate_limits: {
      public: '50 requests/hour',
      authenticated: '500 requests/hour (with API key)',
    },
    supported_features: ['keyword_search', 'category_filtering', 'status_filtering'],
    status: 'active',
  },
  samgov: {
    id: 'samgov',
    name: 'SAM.gov',
    description: 'US Federal Contract Opportunities',
    api_url: 'https://api.sam.gov/prod/opportunities/v2/search',
    documentation_url: 'https://open.gsa.gov/api/',
    requires_api_key: true,
    rate_limits: {
      authenticated: '1000 requests/day, 10 requests/second',
    },
    supported_features: ['keyword_search', 'date_filtering', 'agency_filtering', 'naics_filtering'],
    status: 'active',
  },
  worldbank: {
    id: 'worldbank',
    name: 'World Bank',
    description: 'World Bank Procurement Opportunities',
    api_url: 'https://api.worldbank.org/v2/procurement',
    documentation_url: 'https://datahelpdesk.worldbank.org/knowledgebase/articles/889392',
    requires_api_key: false,
    rate_limits: {
      public: '100 requests/hour',
      authenticated: '1000 requests/hour (with API key)',
    },
    supported_features: ['keyword_search', 'country_filtering', 'sector_filtering'],
    status: 'active',
  },
  usa_awards: {
    id: 'usa_awards',
    name: 'US Government Awards',
    description: 'USASpending API - Federal Awards and Contracts (FPDS)',
    api_url: 'https://api.usaspending.gov/api/v2',
    documentation_url: 'https://api.usaspending.gov/',
    requires_api_key: false,
    rate_limits: {
      public: '1000 requests/day',
    },
    supported_features: ['keyword_search', 'agency_filtering', 'date_filtering', 'award_type_filtering'],
    status: 'active',
  },
  usa_budgets: {
    id: 'usa_budgets',
    name: 'Department Budgets',
    description: 'USASpending API - Federal Department Budgets and Finance',
    api_url: 'https://api.usaspending.gov/api/v2',
    documentation_url: 'https://api.usaspending.gov/',
    requires_api_key: false,
    rate_limits: {
      public: '1000 requests/day',
    },
    supported_features: ['agency_filtering', 'fiscal_year_filtering', 'budget_function_filtering'],
    status: 'active',
  },
  federal_register: {
    id: 'federal_register',
    name: 'Federal Register',
    description: 'Federal Register - Laws, Regulations, and Notices',
    api_url: 'https://www.federalregister.gov/api/v1',
    documentation_url: 'https://www.federalregister.gov/developers/documentation/api/v1',
    requires_api_key: false,
    rate_limits: {
      public: '1000 requests/hour',
    },
    supported_features: ['keyword_search', 'agency_filtering', 'type_filtering', 'date_filtering'],
    status: 'active',
  },
  executive_orders: {
    id: 'executive_orders',
    name: 'Executive Orders',
    description: 'Federal Register - Presidential Executive Orders',
    api_url: 'https://www.federalregister.gov/api/v1',
    documentation_url: 'https://www.federalregister.gov/developers/documentation/api/v1',
    requires_api_key: false,
    rate_limits: {
      public: '1000 requests/hour',
    },
    supported_features: ['keyword_search', 'president_filtering', 'date_filtering'],
    status: 'active',
  },
  rss_feeds: {
    id: 'rss_feeds',
    name: 'RSS News Feeds',
    description: 'Live RSS feeds for government and policy news tracking',
    api_url: 'https://www.whitehouse.gov/feed/',
    documentation_url: 'https://en.wikipedia.org/wiki/RSS',
    requires_api_key: false,
    rate_limits: {
      public: 'Unlimited (per feed)',
    },
    supported_features: ['keyword_filtering', 'source_filtering', 'date_filtering'],
    status: 'active',
  },
  bc_bid: {
    id: 'bc_bid',
    name: 'BC Bid',
    description: 'British Columbia Government Procurement Portal',
    api_url: 'https://www.bcbid.gov.bc.ca/',
    documentation_url: 'https://www.bcbid.gov.bc.ca/',
    requires_api_key: true, // Likely requires authentication
    rate_limits: {
      authenticated: 'TBD - requires credential testing',
    },
    supported_features: ['keyword_search', 'date_filtering', 'category_filtering', 'location_filtering'],
    status: 'beta', // Beta until backend implementation is complete
  },
  bids_tender: {
    id: 'bids_tender',
    name: 'Bids&Tender',
    description: 'Canadian Procurement Portal Aggregator',
    api_url: 'https://www.bidsandtender.com/',
    documentation_url: 'https://www.bidsandtender.com/',
    requires_api_key: true, // Likely requires authentication
    rate_limits: {
      authenticated: 'TBD - requires credential testing',
    },
    supported_features: ['keyword_search', 'date_filtering', 'category_filtering', 'location_filtering'],
    status: 'beta', // Beta until backend implementation is complete
  },
  bonfire: {
    id: 'bonfire',
    name: 'Bonfire',
    description: 'Enterprise Procurement Platform',
    api_url: 'https://www.gobonfire.com/',
    documentation_url: 'https://www.gobonfire.com/support', // TBD - verify actual API docs URL
    requires_api_key: true, // API available, requires authentication
    rate_limits: {
      authenticated: 'TBD - requires credential testing',
    },
    supported_features: ['keyword_search', 'date_filtering', 'category_filtering', 'healthcare_filtering', 'multi_phase_projects'],
    status: 'beta', // Beta until backend implementation is complete
  },
  gbof: {
    id: 'gbof',
    name: 'GBOF',
    description: 'CCC Global Bid Opportunity Finder — international tender aggregator (app.gbof.ca)',
    api_url: '/api/data-sources/gbof',
    documentation_url: 'https://www.gbof.ca/',
    requires_api_key: false,
    rate_limits: {
      authenticated: 'Session login required (GBOF_EMAIL / GBOF_PASSWORD)',
    },
    supported_features: ['keyword_search', 'country_filtering', 'tender_type_filtering', 'pagination'],
    status: 'active',
  },
  merx: {
    id: 'merx',
    name: 'Merx',
    description: 'Canadian Procurement Portal',
    api_url: 'https://www.merx.com/',
    documentation_url: 'https://www.merx.com/',
    requires_api_key: true, // Likely requires authentication
    rate_limits: {
      authenticated: 'TBD - requires credential testing',
    },
    supported_features: ['keyword_search', 'date_filtering', 'category_filtering', 'location_filtering'],
    status: 'beta', // Beta until backend implementation is complete
  },
  sap_ariba: {
    id: 'sap_ariba',
    name: 'SAP Ariba',
    description: 'Enterprise Procurement Platform',
    api_url: 'https://api.ariba.com/',
    documentation_url: 'https://www.ariba.com/', // TBD - verify actual API docs URL
    requires_api_key: true, // API available, requires authentication
    rate_limits: {
      authenticated: 'TBD - requires credential testing',
    },
    supported_features: ['keyword_search', 'date_filtering', 'category_filtering', 'enterprise_filtering'],
    status: 'beta', // Beta until backend implementation is complete
  },
  jaggaer: {
    id: 'jaggaer',
    name: 'Ontario Provincial Portal',
    description: 'Ontario Tenders Portal (Jaggaer ESOP) - Provincial procurement',
    api_url: 'https://ontariotenders.app.jaggaer.com',
    documentation_url: 'https://ontariotenders.app.jaggaer.com',
    requires_api_key: false,
    rate_limits: {
      public: 'Public listing scrape via backend proxy',
    },
    supported_features: ['keyword_search', 'date_filtering', 'tab_filtering'],
    status: 'active',
  },
  manitoba: {
    id: 'manitoba',
    name: 'Manitoba Provincial Portal',
    description: 'Manitoba Tendering Portal (MERX) - Provincial procurement',
    api_url: 'https://www.merx.com/mbgov',
    documentation_url: 'https://www.merx.com/mbgov',
    requires_api_key: false,
    rate_limits: {
      public: 'Public listing scrape via backend proxy',
    },
    supported_features: ['keyword_search', 'date_filtering'],
    status: 'active',
  },
  saskatchewan: {
    id: 'saskatchewan',
    name: 'Saskatchewan Provincial Portal',
    description: 'SaskTenders - Government of Saskatchewan procurement',
    api_url: 'https://www.sasktenders.ca',
    documentation_url: 'https://www.sasktenders.ca',
    requires_api_key: false,
    rate_limits: {
      public: 'Public listing scrape via backend proxy',
    },
    supported_features: ['keyword_search', 'status_filtering'],
    status: 'active',
  },
  nrc: {
    id: 'nrc',
    name: 'Natural Resources Canada',
    description: 'NRCan current funding opportunities and grants',
    api_url: 'https://natural-resources.canada.ca/funding-partnerships/current-funding-opportunities',
    documentation_url: 'https://natural-resources.canada.ca',
    requires_api_key: false,
    rate_limits: {
      public: 'Public listing scrape via backend proxy',
    },
    supported_features: ['keyword_search', 'detail_pages'],
    status: 'active',
  },
  toronto: {
    id: 'toronto',
    name: 'Toronto Portal',
    description: 'City of Toronto Bids Portal - municipal procurement',
    api_url: 'https://www.toronto.ca/business-economy/doing-business-with-the-city/searching-bidding-on-city-contracts/toronto-bids-portal/',
    documentation_url: 'https://www.toronto.ca/business-economy/doing-business-with-the-city/searching-bidding-on-city-contracts/toronto-bids-portal/',
    requires_api_key: false,
    rate_limits: {
      public: 'Public OData API via backend proxy',
    },
    supported_features: ['keyword_search', 'date_filtering', 'status_filtering', 'category_filtering'],
    status: 'active',
  },
  ungm: {
    id: 'ungm',
    name: 'UNGM',
    description: 'United Nations Global Marketplace - international UN procurement notices',
    api_url: 'https://www.ungm.org/public/notice',
    documentation_url: 'https://www.ungm.org/public/notice',
    requires_api_key: false,
    rate_limits: {
      public: 'Public HTML search API via backend proxy (15 rows per scroll batch)',
    },
    supported_features: ['keyword_search', 'date_filtering', 'status_filtering', 'agency_filtering'],
    status: 'active',
  },
  adb: {
    id: 'adb',
    name: 'Asian Development Bank',
    description: 'Asian Development Bank - international development procurement tenders',
    api_url: 'https://www.adb.org/projects/tenders',
    documentation_url: 'https://www.adb.org/projects/tenders',
    requires_api_key: false,
    rate_limits: {
      public: 'SearchStax Solr JSON API via backend proxy (12 rows per batch)',
    },
    supported_features: ['keyword_search', 'date_filtering', 'status_filtering', 'sector_filtering', 'country_filtering'],
    status: 'active',
  },
  afdb: {
    id: 'afdb',
    name: 'African Development Bank',
    description: 'African Development Bank Group - corporate procurement solicitations',
    api_url: 'https://www.afdb.org/en/about-us/corporate-procurement/procurement-notices/current-solicitations',
    documentation_url: 'https://www.afdb.org/en/about-us/corporate-procurement/procurement-notices/current-solicitations',
    requires_api_key: false,
    rate_limits: {
      public: 'Playwright scraper via backend proxy (18 cards per page, Cloudflare-protected)',
    },
    supported_features: ['keyword_search', 'date_filtering', 'country_filtering', 'sector_filtering'],
    status: 'active',
  },
  city_portals: {
    id: 'city_portals',
    name: 'City Portals',
    description: 'Municipal Procurement Portals (RSS Feeds)',
    api_url: 'varies_by_city', // Each city has its own URL
    documentation_url: 'varies_by_city',
    requires_api_key: false, // Most city portals are public
    rate_limits: {
      public: 'TBD - requires testing per city',
    },
    supported_features: ['keyword_search', 'date_filtering', 'category_filtering', 'city_filtering', 'location_filtering'],
    status: 'active',
  },
  vancouver_city_portal: {
    id: 'vancouver_city_portal',
    name: 'Vancouver City Portal',
    description: 'City of Vancouver Procurement Opportunities (Jaggaer)',
    api_url: 'https://bids01.jaggaer.com/apps/Router/PublicEvent',
    documentation_url: 'https://bids01.jaggaer.com/',
    requires_api_key: false, // Uses cookies for authentication
    rate_limits: {
      public: 'TBD - requires testing',
    },
    supported_features: ['keyword_search', 'date_filtering', 'type_filtering'],
    status: 'active',
  },
  victoria_city_portal: {
    id: 'victoria_city_portal',
    name: 'Victoria City Portal',
    description: 'City of Victoria Procurement Opportunities (Bonfire)',
    api_url: 'https://victoria.bonfirehub.ca/PublicPortal/getOpenPublicOpportunitiesSectionData',
    documentation_url: 'https://victoria.bonfirehub.ca/',
    requires_api_key: false, // Uses cookies for authentication
    rate_limits: {
      public: 'TBD - requires testing',
    },
    supported_features: ['keyword_search', 'date_filtering'],
    status: 'active',
  },
  metrovancouver_city_portal: {
    id: 'metrovancouver_city_portal',
    name: 'Metro Vancouver City Portal',
    description: 'Metro Vancouver Regional District Procurement Opportunities (BidsAndTenders)',
    api_url: 'https://metrovancouver.bidsandtenders.ca/Module/Tenders/en/Tender/Search/bf0e4358-a83e-4c2b-82c7-adae3b4a8fd5',
    documentation_url: 'https://metrovancouver.bidsandtenders.ca/',
    requires_api_key: true, // Uses cookies and CSRF token for authentication
    rate_limits: {
      authenticated: 'TBD - requires testing',
    },
    supported_features: ['keyword_search', 'date_filtering', 'status_filtering'],
    status: 'active',
  },
  // India country portals (sync via backend /api/data-sources/...)
  tender_247: { id: 'tender_247', name: 'Tender247', description: 'India - Tender247 tenders', api_url: '/api/data-sources/tender247/tenders', documentation_url: 'https://www.tender247.com', requires_api_key: false, rate_limits: { public: 'Backend proxy' }, supported_features: ['keyword_search'], status: 'active' as const },
  tendertiger_india: { id: 'tendertiger_india', name: 'TenderTiger India', description: 'India - TenderTiger India tenders', api_url: '/api/data-sources/tendertiger-india', documentation_url: 'https://www.tendertiger.com', requires_api_key: false, rate_limits: { public: 'Backend proxy' }, supported_features: ['keyword_search'], status: 'active' as const },
  eprocure: { id: 'eprocure', name: 'eProcure', description: 'India - CPPPC eProcure tenders', api_url: '/api/data-sources/eprocure/tenders', documentation_url: '', requires_api_key: false, rate_limits: { public: 'Backend proxy' }, supported_features: ['keyword_search'], status: 'active' as const },
  gem: { id: 'gem', name: 'GEM', description: 'India - Government e-Marketplace bids', api_url: '/api/data-sources/gem/bids', documentation_url: 'https://gem.gov.in', requires_api_key: false, rate_limits: { public: 'Backend proxy' }, supported_features: ['keyword_search'], status: 'active' as const },
  hp_tenders: { id: 'hp_tenders', name: 'HP Tenders', description: 'India - Himachal Pradesh tenders', api_url: '/api/data-sources/hp/tenders', documentation_url: '', requires_api_key: false, rate_limits: { public: 'Backend proxy' }, supported_features: ['keyword_search'], status: 'active' as const },
  tender_odisha: { id: 'tender_odisha', name: 'Tender Odisha', description: 'India - Odisha state tenders', api_url: '/api/data-sources/odisha/tenders', documentation_url: '', requires_api_key: false, rate_limits: { public: 'Backend proxy' }, supported_features: ['keyword_search'], status: 'active' as const },
  uk_tenders: { id: 'uk_tenders', name: 'UK Tenders (India)', description: 'India - Uttarakhand tenders', api_url: '/api/data-sources/uk/tenders', documentation_url: '', requires_api_key: false, rate_limits: { public: 'Backend proxy' }, supported_features: ['keyword_search'], status: 'active' as const },
  // Foreign country portals (TenderTiger)
  tendertiger_brazil: { id: 'tendertiger_brazil', name: 'TenderTiger Brazil', description: 'Brazil tenders via TenderTiger', api_url: '/api/data-sources/tendertiger-brazil', documentation_url: 'https://www.tendertiger.com', requires_api_key: false, rate_limits: { public: 'Backend proxy' }, supported_features: ['keyword_search'], status: 'active' as const },
  tendertiger_canada: { id: 'tendertiger_canada', name: 'TenderTiger Canada', description: 'Canada tenders via TenderTiger', api_url: '/api/data-sources/tendertiger-canada', documentation_url: 'https://www.tendertiger.com', requires_api_key: false, rate_limits: { public: 'Backend proxy' }, supported_features: ['keyword_search'], status: 'active' as const },
  tendertiger_ethiopia: { id: 'tendertiger_ethiopia', name: 'TenderTiger Ethiopia', description: 'Ethiopia tenders via TenderTiger', api_url: '/api/data-sources/tendertiger-ethiopia', documentation_url: 'https://www.tendertiger.com', requires_api_key: false, rate_limits: { public: 'Backend proxy' }, supported_features: ['keyword_search'], status: 'active' as const },
  tendertiger_indonesia: { id: 'tendertiger_indonesia', name: 'TenderTiger Indonesia', description: 'Indonesia tenders via TenderTiger', api_url: '/api/data-sources/tendertiger-indonesia', documentation_url: 'https://www.tendertiger.com', requires_api_key: false, rate_limits: { public: 'Backend proxy' }, supported_features: ['keyword_search'], status: 'active' as const },
  tendertiger_kenya: { id: 'tendertiger_kenya', name: 'TenderTiger Kenya', description: 'Kenya tenders via TenderTiger', api_url: '/api/data-sources/tendertiger-kenya', documentation_url: 'https://www.tendertiger.com', requires_api_key: false, rate_limits: { public: 'Backend proxy' }, supported_features: ['keyword_search'], status: 'active' as const },
  tendertiger_mexico: { id: 'tendertiger_mexico', name: 'TenderTiger Mexico', description: 'Mexico tenders via TenderTiger', api_url: '/api/data-sources/tendertiger-mexico', documentation_url: 'https://www.tendertiger.com', requires_api_key: false, rate_limits: { public: 'Backend proxy' }, supported_features: ['keyword_search'], status: 'active' as const },
  tendertiger_peru: { id: 'tendertiger_peru', name: 'TenderTiger Peru', description: 'Peru tenders via TenderTiger', api_url: '/api/data-sources/tendertiger-peru', documentation_url: 'https://www.tendertiger.com', requires_api_key: false, rate_limits: { public: 'Backend proxy' }, supported_features: ['keyword_search'], status: 'active' as const },
  tendertiger_south_africa: { id: 'tendertiger_south_africa', name: 'TenderTiger South Africa', description: 'South Africa tenders via TenderTiger', api_url: '/api/data-sources/tendertiger-south-africa', documentation_url: 'https://www.tendertiger.com', requires_api_key: false, rate_limits: { public: 'Backend proxy' }, supported_features: ['keyword_search'], status: 'active' as const },
  tendertiger_tanzania: { id: 'tendertiger_tanzania', name: 'TenderTiger Tanzania', description: 'Tanzania tenders via TenderTiger', api_url: '/api/data-sources/tendertiger-tanzania', documentation_url: 'https://www.tendertiger.com', requires_api_key: false, rate_limits: { public: 'Backend proxy' }, supported_features: ['keyword_search'], status: 'active' as const },
  tendertiger_usa: { id: 'tendertiger_usa', name: 'TenderTiger USA', description: 'USA tenders via TenderTiger', api_url: '/api/data-sources/tendertiger-usa', documentation_url: 'https://www.tendertiger.com', requires_api_key: false, rate_limits: { public: 'Backend proxy' }, supported_features: ['keyword_search'], status: 'active' as const },
};

// ============================================================================
// Validation Functions
// ============================================================================

function validateDataSourceConfig(config: DataSourceConfig, metadata: DataSourceMetadata): ValidationResult {
  const errors: string[] = [];
  const warnings: string[] = [];

  // Check if enabled
  if (!config.enabled) {
    warnings.push('Data source is disabled');
  }

  // Check API key requirement (legacy support)
  if (metadata.requires_api_key && !config.api_key && !config.credentials?.api_key) {
    errors.push(`API key is required for ${metadata.name}`);
  }

  // Validate credentials if data source requires them
  if (requiresCredentials(metadata.id)) {
    const credentialFields = getCredentialFields(metadata.id);
    const credentials = config.credentials || {};
    
    for (const field of credentialFields) {
      if (field.required) {
        // Check in credentials object first, then fall back to legacy api_key
        const value = credentials[field.id] || 
                     (field.id === 'api_key' && config.api_key ? config.api_key : undefined);
        
        if (!value || value.trim() === '') {
          errors.push(`${field.label} is required for ${metadata.name}`);
        }
      }
    }
  }

  // Validate API URL
  if (config.api_url) {
    try {
      new URL(config.api_url);
    } catch (e) {
      errors.push(`Invalid API URL: ${config.api_url}`);
    }
  }

  // Validate sync frequency
  const validFrequencies = ['hourly', 'daily', 'weekly', 'monthly'];
  if (!validFrequencies.includes(config.sync_frequency)) {
    errors.push(`Invalid sync frequency: ${config.sync_frequency}. Must be one of: ${validFrequencies.join(', ')}`);
  }

  // Validate filters
  if (config.filters) {
    if (config.filters.keywords && !Array.isArray(config.filters.keywords)) {
      errors.push('filters.keywords must be an array');
    }
    if (config.filters.service_lines && !Array.isArray(config.filters.service_lines)) {
      errors.push('filters.service_lines must be an array');
    }
    if (config.filters.min_value !== undefined && typeof config.filters.min_value !== 'number') {
      errors.push('filters.min_value must be a number');
    }
    if (config.filters.max_value !== undefined && typeof config.filters.max_value !== 'number') {
      errors.push('filters.max_value must be a number');
    }
  }

  return {
    valid: errors.length === 0,
    errors,
    warnings,
  };
}

// ============================================================================
// Health Check Functions
// ============================================================================

async function testDataSourceConnection(
  metadata: DataSourceMetadata,
  config: DataSourceConfig
): Promise<HealthCheckResult> {
  const startTime = Date.now();
  
  try {
    // Special handling for different data sources
    let url = config.api_url || metadata.api_url;
    
    // For SAM.gov, use backend proxy endpoint to avoid CORS
    if (metadata.id === 'samgov') {
      // Use backend proxy endpoint instead of direct API call
      // const { getApiBaseUrl } = await import('@tracekit/core');
      // const apiBaseUrl = getApiBaseUrl();
      const apiBaseUrl = (typeof process !== 'undefined' && process.env?.VITE_API_URL) || 
                         (typeof window !== 'undefined' && (window as any).__API_URL__) || 
                         'http://localhost:8000';
      url = `${apiBaseUrl}/api/data-sources/samgov/health`;
    }

    // GBOF session auth runs on backend (ALTCHA PoW + $loadTenders RPC)
    if (metadata.id === 'gbof') {
      const apiBaseUrl = (typeof process !== 'undefined' && process.env?.VITE_API_URL) ||
                         (typeof window !== 'undefined' && (window as any).__API_URL__) ||
                         'http://localhost:8000';
      url = `${apiBaseUrl}/api/data-sources/gbof/health`;
    }
    
    // For Canada Grants, use the correct search endpoint
    if (metadata.id === 'canada_grants') {
      url = 'https://open.canada.ca/data/api/3/action/package_search?q=grants&rows=1';
    }
    
    // For Canada Tenders, skip health check (CORS issues, but works in preview)
    if (metadata.id === 'canada_tenders') {
      return {
        healthy: true,
        status: 'ok',
        message: `${metadata.name} configured (CORS may limit browser testing, but preview works)`,
        response_time_ms: Date.now() - startTime,
        last_checked: new Date().toISOString(),
      };
    }
    
    // For World Bank, use correct endpoint
    if (metadata.id === 'worldbank') {
      url = 'https://api.worldbank.org/v2/procurement?format=json&per_page=1';
    }
    
    // For USA Spending, use correct endpoint
    if (metadata.id === 'usa_awards' || metadata.id === 'usa_budgets') {
      url = 'https://api.usaspending.gov/api/v2/search/spending_by_award/?limit=1';
    }
    
    // For Federal Register and Executive Orders, use correct endpoint
    if (metadata.id === 'federal_register' || metadata.id === 'executive_orders') {
      url = 'https://www.federalregister.gov/api/v1/documents.json?per_page=1';
    }
    
    // For RSS feeds, just validate config
    if (metadata.id === 'rss_feeds') {
      return {
        healthy: true,
        status: 'ok',
        message: `${metadata.name} configured`,
        response_time_ms: Date.now() - startTime,
        last_checked: new Date().toISOString(),
      };
    }
    
    if (!url) {
      return {
        healthy: false,
        status: 'error',
        message: `No API URL configured for ${metadata.name}`,
        response_time_ms: Date.now() - startTime,
        last_checked: new Date().toISOString(),
      };
    }
    
    const headers: Record<string, string> = {
      'Accept': 'application/json',
      'User-Agent': 'TraceOS-RFP-Agent/1.0',
    };

    // Add authentication headers based on source
    if (config.api_key) {
      if (metadata.id === 'samgov') {
        headers['X-Api-Key'] = config.api_key;
      } else {
        headers['Authorization'] = `Bearer ${config.api_key}`;
      }
    }

    // Create timeout manually (AbortSignal.timeout not available in all browsers)
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 5000);

    try {
      // Make a minimal test request
      const response = await fetch(url, {
        method: 'GET',
        headers,
        signal: controller.signal,
      });

      clearTimeout(timeoutId);
      const responseTime = Date.now() - startTime;

      if (response.ok) {
        return {
          healthy: true,
          status: 'ok',
          message: `Successfully connected to ${metadata.name}`,
          response_time_ms: responseTime,
          last_checked: new Date().toISOString(),
        };
      } else if (response.status === 429) {
        return {
          healthy: false,
          status: 'degraded',
          message: `Rate limit exceeded for ${metadata.name}`,
          response_time_ms: responseTime,
          last_checked: new Date().toISOString(),
        };
      } else if (response.status === 401 || response.status === 403) {
        return {
          healthy: false,
          status: 'error',
          message: `Authentication failed for ${metadata.name}`,
          response_time_ms: responseTime,
          last_checked: new Date().toISOString(),
        };
      } else if (response.status === 404) {
        // 404 is OK - means the API is reachable, just the endpoint might need specific path
        return {
          healthy: true,
          status: 'ok',
          message: `${metadata.name} API reachable (endpoint may require specific path)`,
          response_time_ms: responseTime,
          last_checked: new Date().toISOString(),
        };
      } else {
        return {
          healthy: false,
          status: 'error',
          message: `HTTP ${response.status}: ${response.statusText}`,
          response_time_ms: responseTime,
          last_checked: new Date().toISOString(),
        };
      }
    } catch (fetchError: any) {
      clearTimeout(timeoutId);
      const responseTime = Date.now() - startTime;
      
      if (fetchError.name === 'AbortError') {
        return {
          healthy: false,
          status: 'error',
          message: `Timeout connecting to ${metadata.name}`,
          response_time_ms: responseTime,
          last_checked: new Date().toISOString(),
        };
      }
      
      // CORS or network error - might be expected for some APIs in browser
      if (fetchError.message?.includes('CORS') || fetchError.message?.includes('Failed to fetch') || fetchError.message?.includes('NetworkError')) {
        // For browser environments, CORS errors are common - mark as healthy if config is valid
        // The API works, just can't test from browser due to CORS
        return {
          healthy: true,
          status: 'ok',
          message: `${metadata.name} configured (CORS may limit browser testing)`,
          response_time_ms: responseTime,
          last_checked: new Date().toISOString(),
        };
      }
      
      throw fetchError;
    }
  } catch (error: any) {
    const responseTime = Date.now() - startTime;
    return {
      healthy: false,
      status: 'error',
      message: error.message || 'Connection failed',
      response_time_ms: responseTime,
      last_checked: new Date().toISOString(),
    };
  }
}

// ============================================================================
// Data Source Runners
// ============================================================================

const DATA_SOURCE_RUNNERS = {
  canada_grants: {
    fetch: fetchCanadaGrants,
    validate: (config) => validateDataSourceConfig(config, DATA_SOURCE_METADATA.canada_grants),
    test: (config) => testDataSourceConnection(DATA_SOURCE_METADATA.canada_grants, config),
  },
  canada_tenders: {
    fetch: fetchCanadaTenders,
    validate: (config) => validateDataSourceConfig(config, DATA_SOURCE_METADATA.canada_tenders),
    test: (config) => testDataSourceConnection(DATA_SOURCE_METADATA.canada_tenders, config),
  },
  samgov: {
    fetch: fetchSAMGov,
    validate: (config) => validateDataSourceConfig(config, DATA_SOURCE_METADATA.samgov),
    test: (config) => testDataSourceConnection(DATA_SOURCE_METADATA.samgov, config),
  },
  worldbank: {
    fetch: fetchWorldBank,
    validate: (config) => validateDataSourceConfig(config, DATA_SOURCE_METADATA.worldbank),
    test: (config) => testDataSourceConnection(DATA_SOURCE_METADATA.worldbank, config),
  },
  usa_awards: {
    fetch: fetchUSAwards,
    validate: (config) => validateDataSourceConfig(config, DATA_SOURCE_METADATA.usa_awards),
    test: (config) => testDataSourceConnection(DATA_SOURCE_METADATA.usa_awards, config),
  },
  usa_budgets: {
    fetch: async (config, projectId) => {
      const budgets = await fetchDepartmentBudgets(config, projectId);
      // Convert budgets to opportunities format for consistency
      return {
        opportunities: budgets.map(b => ({
          id: `budget-${b.agency_code}-${b.fiscal_year}`,
          source: 'usa_budgets' as const,
          title: `${b.agency_name} FY${b.fiscal_year} Budget`,
          description: `Total Budgetary Resources: $${b.total_budgetary_resources.toLocaleString()}, Obligations: $${b.total_obligations.toLocaleString()}`,
          organization: b.agency_name,
          deadline: `${b.fiscal_year}-09-30`,
          value: `$${b.total_budgetary_resources.toLocaleString()}`,
          url: `https://www.usaspending.gov/agency/${b.agency_code}`,
          keywords: [b.agency_name.toLowerCase(), 'budget', 'finance'],
          service_lines: ['General'],
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        })),
        awards: [],
      };
    },
    validate: (config) => validateDataSourceConfig(config, DATA_SOURCE_METADATA.usa_budgets),
    test: (config) => testDataSourceConnection(DATA_SOURCE_METADATA.usa_budgets, config),
  },
  federal_register: {
    fetch: async (config, projectId) => {
      const documents = await fetchFederalRegisterDocuments(config, projectId);
      return {
        opportunities: documents.map(doc => ({
          id: `fr-${doc.document_number}`,
          source: 'federal_register' as const,
          title: doc.title,
          description: doc.summary || '',
          organization: doc.agencies.join(', ') || 'Federal Government',
          deadline: doc.publication_date,
          url: doc.html_url,
          keywords: extractKeywords(doc.title + ' ' + (doc.summary || '')),
          service_lines: matchServiceLines(doc.title + ' ' + (doc.summary || '')),
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        })),
        awards: [],
      };
    },
    validate: (config) => validateDataSourceConfig(config, DATA_SOURCE_METADATA.federal_register),
    test: (config) => testDataSourceConnection(DATA_SOURCE_METADATA.federal_register, config),
  },
  executive_orders: {
    fetch: async (config, projectId) => {
      const orders = await fetchExecutiveOrders(config, projectId);
      return {
        opportunities: orders.map(eo => ({
          id: `eo-${eo.document_number}`,
          source: 'executive_orders' as const,
          title: eo.title,
          description: eo.summary || '',
          organization: eo.agencies.join(', ') || 'Executive Office of the President',
          deadline: eo.publication_date,
          url: eo.html_url,
          keywords: extractKeywords(eo.title + ' ' + (eo.summary || '')),
          service_lines: matchServiceLines(eo.title + ' ' + (eo.summary || '')),
          reference_number: eo.executive_order_number || eo.document_number,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        })),
        awards: [],
      };
    },
    validate: (config) => validateDataSourceConfig(config, DATA_SOURCE_METADATA.executive_orders),
    test: (config) => testDataSourceConnection(DATA_SOURCE_METADATA.executive_orders, config),
  },
  rss_feeds: {
    fetch: async (config, projectId) => {
      const items = await fetchRSSFeeds(config, projectId);
      return {
        opportunities: items.map(item => ({
          id: item.id,
          source: 'rss_feeds' as const,
          title: item.title,
          description: item.description,
          organization: item.source,
          deadline: item.pubDate,
          url: item.link,
          keywords: extractKeywords(item.title + ' ' + item.description),
          service_lines: matchServiceLines(item.title + ' ' + item.description),
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        })),
        awards: [],
      };
    },
    validate: (config) => validateDataSourceConfig(config, DATA_SOURCE_METADATA.rss_feeds),
    test: (config) => testDataSourceConnection(DATA_SOURCE_METADATA.rss_feeds, config),
  },
  bc_bid: {
    fetch: fetchBCBid,
    validate: (config) => validateDataSourceConfig(config, DATA_SOURCE_METADATA.bc_bid),
    test: (config) => testDataSourceConnection(DATA_SOURCE_METADATA.bc_bid, config),
  },
  bids_tender: {
    fetch: fetchBidsTender,
    validate: (config) => validateDataSourceConfig(config, DATA_SOURCE_METADATA.bids_tender),
    test: (config) => testDataSourceConnection(DATA_SOURCE_METADATA.bids_tender, config),
  },
  bonfire: {
    fetch: fetchBonfire,
    validate: (config) => validateDataSourceConfig(config, DATA_SOURCE_METADATA.bonfire),
    test: (config) => testDataSourceConnection(DATA_SOURCE_METADATA.bonfire, config),
  },
  gbof: {
    fetch: async () => ({ opportunities: [], awards: [] }),
    validate: (config) => validateDataSourceConfig(config, DATA_SOURCE_METADATA.gbof),
    test: (config) => testDataSourceConnection(DATA_SOURCE_METADATA.gbof, config),
  },
  merx: {
    fetch: fetchMerx,
    validate: (config) => validateDataSourceConfig(config, DATA_SOURCE_METADATA.merx),
    test: (config) => testDataSourceConnection(DATA_SOURCE_METADATA.merx, config),
  },
  sap_ariba: {
    fetch: fetchSAPAriba,
    validate: (config) => validateDataSourceConfig(config, DATA_SOURCE_METADATA.sap_ariba),
    test: (config) => testDataSourceConnection(DATA_SOURCE_METADATA.sap_ariba, config),
  },
  jaggaer: {
    fetch: fetchJaggaer,
    validate: (config) => validateDataSourceConfig(config, DATA_SOURCE_METADATA.jaggaer),
    test: (config) => testDataSourceConnection(DATA_SOURCE_METADATA.jaggaer, config),
  },
  manitoba: {
    fetch: fetchManitoba,
    validate: (config) => validateDataSourceConfig(config, DATA_SOURCE_METADATA.manitoba),
    test: (config) => testDataSourceConnection(DATA_SOURCE_METADATA.manitoba, config),
  },
  saskatchewan: {
    fetch: fetchSaskatchewan,
    validate: (config) => validateDataSourceConfig(config, DATA_SOURCE_METADATA.saskatchewan),
    test: (config) => testDataSourceConnection(DATA_SOURCE_METADATA.saskatchewan, config),
  },
  nrc: {
    fetch: fetchNrc,
    validate: (config) => validateDataSourceConfig(config, DATA_SOURCE_METADATA.nrc),
    test: (config) => testDataSourceConnection(DATA_SOURCE_METADATA.nrc, config),
  },
  toronto: {
    fetch: fetchToronto,
    validate: (config) => validateDataSourceConfig(config, DATA_SOURCE_METADATA.toronto),
    test: (config) => testDataSourceConnection(DATA_SOURCE_METADATA.toronto, config),
  },
  ungm: {
    fetch: fetchUngm,
    validate: (config) => validateDataSourceConfig(config, DATA_SOURCE_METADATA.ungm),
    test: (config) => testDataSourceConnection(DATA_SOURCE_METADATA.ungm, config),
  },
  adb: {
    fetch: fetchAdb,
    validate: (config) => validateDataSourceConfig(config, DATA_SOURCE_METADATA.adb),
    test: (config) => testDataSourceConnection(DATA_SOURCE_METADATA.adb, config),
  },
  afdb: {
    fetch: fetchAfdb,
    validate: (config) => validateDataSourceConfig(config, DATA_SOURCE_METADATA.afdb),
    test: (config) => testDataSourceConnection(DATA_SOURCE_METADATA.afdb, config),
  },
  city_portals: {
    fetch: fetchCityPortals,
    validate: (config) => validateDataSourceConfig(config, DATA_SOURCE_METADATA.city_portals),
    test: (config) => testDataSourceConnection(DATA_SOURCE_METADATA.city_portals, config),
  },
  vancouver_city_portal: {
    fetch: fetchVancouverCityPortal,
    validate: (config) => validateDataSourceConfig(config, DATA_SOURCE_METADATA.vancouver_city_portal),
    test: (config) => testDataSourceConnection(DATA_SOURCE_METADATA.vancouver_city_portal, config),
  },
  victoria_city_portal: {
    fetch: fetchVictoriaCityPortal,
    validate: (config) => validateDataSourceConfig(config, DATA_SOURCE_METADATA.victoria_city_portal),
    test: (config) => testDataSourceConnection(DATA_SOURCE_METADATA.victoria_city_portal, config),
  },
  metrovancouver_city_portal: {
    fetch: fetchMetroVancouverCityPortal,
    validate: (config) => validateDataSourceConfig(config, DATA_SOURCE_METADATA.metrovancouver_city_portal),
    test: (config) => testDataSourceConnection(DATA_SOURCE_METADATA.metrovancouver_city_portal, config),
  },
  // India & Foreign country portals: sync via backend API integration; frontend fetch is no-op
  ...(['tender_247', 'tendertiger_india', 'eprocure', 'gem', 'hp_tenders', 'tender_odisha', 'uk_tenders',
    'tendertiger_brazil', 'tendertiger_canada', 'tendertiger_ethiopia', 'tendertiger_indonesia',
    'tendertiger_kenya', 'tendertiger_mexico', 'tendertiger_peru', 'tendertiger_south_africa',
    'tendertiger_tanzania', 'tendertiger_usa'] as const).reduce<Partial<Record<DataSourceType, DataSourceRunner>>>((acc, id) => {
    acc[id] = {
      fetch: async () => ({ opportunities: [], awards: [] }),
      validate: (config) => validateDataSourceConfig(config, DATA_SOURCE_METADATA[id]),
      test: () => Promise.resolve({ healthy: true, status: 'ok' as const, message: `${DATA_SOURCE_METADATA[id].name} configured (sync via backend)`, last_checked: new Date().toISOString() }),
    };
    return acc;
  }, {}),
} as Record<DataSourceType, DataSourceRunner>;

// Helper functions for registry
function extractKeywords(text: string): string[] {
  const commonWords = new Set(['the', 'a', 'an', 'and', 'or', 'but', 'in', 'on', 'at', 'to', 'for', 'of', 'with', 'by']);
  const words = text.toLowerCase()
    .replace(/[^\w\s]/g, ' ')
    .split(/\s+/)
    .filter(word => word.length > 3 && !commonWords.has(word));
  
  return [...new Set(words)].slice(0, 10);
}

/**
 * Match opportunity to service lines
 * 
 * Maps opportunities to the 9 service lines defined in the policy pack:
 * 1. Contaminated Sites
 * 2. Environmental Engineering
 * 3. Biology & EIA
 * 4. Indoor Air Quality
 * 5. EHS Management
 * 6. Spill Response
 * 7. Landfill
 * 8. Forestry
 * 9. Indigenous Engagement
 */
function matchServiceLines(text: string): string[] {
  const serviceLines: string[] = [];
  const lowerText = text.toLowerCase();
  
  const serviceLineKeywords: Record<string, string[]> = {
    'Contaminated Sites': [
      'contaminated sites', 'contamination', 'remediation', 'phase i', 'phase ii', 'phase iii',
      'site assessment', 'environmental site assessment', 'esa', 'phase 1', 'phase 2', 'phase 3',
      'soil remediation', 'groundwater remediation', 'risk assessment', 'csap', 'contaminated site',
      'brownfield', 'petroleum', 'hydrocarbon', 'pah', 'pce', 'tce', 'dce', 'chlorinated', 'solvent'
    ],
    'Environmental Engineering': [
      'environmental engineering', 'engineering', 'design', 'permit', 'compliance', 'monitoring',
      'air quality', 'water quality', 'waste management', 'wastewater', 'stormwater', 'peng',
      'hydrogeology', 'hydrogeological', 'geotechnical', 'geotechnical engineering', 'civil engineering',
      'environmental design', 'remediation design', 'treatment system', 'pump and treat', 'in situ'
    ],
    'Biology & EIA': [
      'biology', 'biological', 'biologist', 'rp.bio', 'rp bio', 'wildlife', 'habitat', 'species at risk',
      'environmental impact assessment', 'eia', 'environmental assessment', 'ea', 'fisheries',
      'aquatic', 'terrestrial', 'vegetation', 'wetland', 'riparian', 'migration', 'nesting', 'breeding',
      'endangered', 'threatened', 'species', 'biodiversity', 'ecosystem', 'ecological'
    ],
    'Indoor Air Quality': [
      'indoor air quality', 'iaq', 'air quality', 'mold', 'mould', 'asbestos', 'lead', 'vocs',
      'volatile organic compounds', 'radon', 'indoor environment', 'air monitoring', 'air sampling',
      'ventilation', 'hvac', 'sterling iaq', 'indoor environmental', 'air testing'
    ],
    'EHS Management': [
      'ehs', 'environmental health safety', 'health and safety', 'occupational health', 'ohs',
      'safety management', 'environmental management', 'compliance', 'regulatory compliance',
      'environmental compliance', 'audit', 'inspection', 'training', 'safety program'
    ],
    'Spill Response': [
      'spill response', 'spill', 'emergency response', 'environmental emergency', 'oil spill',
      'chemical spill', 'hazmat', 'hazardous material', 'emergency cleanup', 'spill cleanup',
      'environmental cleanup', 'incident response', 'emergency management', 'contingency'
    ],
    'Landfill': [
      'landfill', 'waste management', 'solid waste', 'waste disposal', 'waste facility',
      'landfill gas', 'leachate', 'waste diversion', 'waste reduction', 'municipal solid waste',
      'msw', 'waste characterization', 'waste audit', 'waste planning', 'waste strategy'
    ],
    'Forestry': [
      'forestry', 'forest', 'timber', 'logging', 'silviculture', 'forest management', 'forest planning',
      'forest inventory', 'forest assessment', 'tree', 'vegetation management', 'forest health',
      'forest certification', 'sustainable forestry', 'forest operations', 'harvesting'
    ],
    'Indigenous Engagement': [
      'indigenous', 'first nations', 'aboriginal', 'metis', 'inuit', 'indigenous engagement',
      'indigenous consultation', 'traditional knowledge', 'traditional use', 'indigenous relations',
      'indigenous partnership', 'indigenous community', 'treaty', 'land claim', 'reconciliation'
    ],
  };
  
  // Check each service line for keyword matches
  for (const [serviceLine, keywords] of Object.entries(serviceLineKeywords)) {
    if (keywords.some(keyword => lowerText.includes(keyword))) {
      serviceLines.push(serviceLine);
    }
  }
  
  // If no matches, return 'General' as fallback
  return serviceLines.length > 0 ? serviceLines : ['General'];
};

// ============================================================================
// Default Configurations
// ============================================================================

const DEFAULT_CONFIGS: Record<DataSourceType, Partial<DataSourceConfig>> = {
  canada_grants: {
    enabled: true,
    sync_frequency: 'daily',
    filters: {
      keywords: ['environmental', 'assessment', 'remediation', 'contaminated sites'],
      service_lines: ['Contaminated Sites', 'Environmental Engineering'],
    },
  },
  canada_tenders: {
    enabled: true,
    sync_frequency: 'daily',
    filters: {
      keywords: ['environmental', 'assessment', 'remediation'],
    },
  },
  samgov: {
    ...resolveSamGovIntegrationState({ defaultEnabled: true }),
    sync_frequency: 'daily',
    filters: {
      keywords: ['environmental', 'assessment', 'remediation'],
    },
  },
  worldbank: {
    enabled: true,
    sync_frequency: 'weekly',
    filters: {
      keywords: ['environmental', 'assessment'],
    },
  },
  usa_awards: {
    enabled: true,
    sync_frequency: 'daily',
    filters: {
      keywords: ['environmental', 'assessment', 'remediation', 'engineering'],
    },
  },
  usa_budgets: {
    enabled: true,
    sync_frequency: 'daily',
    filters: {},
  },
  federal_register: {
    enabled: true,
    sync_frequency: 'daily',
    filters: {
      keywords: ['environmental', 'procurement', 'contract', 'regulation'],
    },
  },
  executive_orders: {
    enabled: true,
    sync_frequency: 'daily',
    filters: {
      keywords: ['procurement', 'contract', 'technology', 'innovation'],
    },
  },
  rss_feeds: {
    enabled: true,
    sync_frequency: 'hourly',
    filters: {
      keywords: ['government', 'procurement', 'contract', 'federal'],
    },
  },
  bc_bid: {
    enabled: true,
    sync_frequency: 'daily',
    filters: {
      keywords: ['environmental', 'assessment', 'remediation', 'engineering', 'consulting'],
    },
  },
  bids_tender: {
    enabled: true,
    sync_frequency: 'daily',
    filters: {
      keywords: ['environmental', 'assessment', 'remediation', 'engineering', 'consulting'],
    },
  },
  bonfire: {
    enabled: true,
    sync_frequency: 'daily',
    filters: {
      keywords: ['environmental', 'assessment', 'remediation', 'engineering', 'consulting'],
      healthcare: false, // Can be enabled for healthcare-specific filtering
    },
  },
  gbof: {
    enabled: true,
    sync_frequency: 'daily',
    filters: {
      keywords: [],
      tender_type: 'solicitation',
      pages: 5,
      page_size: 20,
    },
  },
  merx: {
    enabled: true,
    sync_frequency: 'daily',
    filters: {
      keywords: ['environmental', 'assessment', 'remediation', 'engineering', 'consulting'],
    },
  },
  sap_ariba: {
    enabled: true,
    sync_frequency: 'daily',
    filters: {
      keywords: ['environmental', 'assessment', 'remediation', 'engineering', 'consulting'],
    },
  },
  jaggaer: {
    enabled: true,
    sync_frequency: 'daily',
    filters: {
      keywords: ['environmental', 'assessment', 'remediation', 'engineering', 'consulting'],
      provinces: ['ON'],
    },
  },
  manitoba: {
    enabled: true,
    sync_frequency: 'daily',
    filters: {
      keywords: ['environmental', 'assessment', 'remediation', 'engineering', 'consulting'],
      provinces: ['MB'],
    },
  },
  saskatchewan: {
    enabled: true,
    sync_frequency: 'daily',
    filters: {
      keywords: ['environmental', 'assessment', 'remediation', 'engineering', 'consulting'],
      provinces: ['SK'],
    },
  },
  nrc: {
    enabled: true,
    sync_frequency: 'daily',
    filters: {
      keywords: ['environmental', 'natural resources', 'funding', 'climate'],
      countries: ['Canada'],
    },
  },
  toronto: {
    enabled: true,
    sync_frequency: 'daily',
    filters: {
      keywords: ['environmental', 'assessment', 'remediation', 'engineering', 'consulting'],
      provinces: ['ON'],
      cities: ['Toronto'],
    },
  },
  ungm: {
    enabled: true,
    sync_frequency: 'daily',
    filters: {
      keywords: ['environmental', 'assessment', 'remediation', 'engineering', 'consulting'],
      countries: ['International'],
      is_active: true,
      limit: 500,
    },
  },
  adb: {
    enabled: true,
    sync_frequency: 'daily',
    filters: {
      keywords: ['environmental', 'assessment', 'remediation', 'engineering', 'consulting'],
      countries: ['International'],
      status: 'Active',
      sort_by: 'deadline',
      limit: 500,
    },
  },
  afdb: {
    enabled: true,
    sync_frequency: 'daily',
    filters: {
      keywords: ['environmental', 'assessment', 'remediation', 'engineering', 'consulting'],
      countries: ['International', 'Africa'],
      limit: 500,
      use_playwright: true,
    },
  },
  city_portals: {
    enabled: true,
    sync_frequency: 'daily',
    filters: {
      keywords: ['environmental', 'assessment', 'remediation', 'engineering', 'consulting'],
      cities: ['Coquitlam', 'Surrey', 'Chilliwack', 'Port Alberni', 'Vancouver', 'Delta', 'Burnaby', 'Victoria'],
    },
  },
  vancouver_city_portal: { enabled: true, sync_frequency: 'daily', filters: { keywords: [] } },
  victoria_city_portal: { enabled: true, sync_frequency: 'daily', filters: { keywords: [] } },
  metrovancouver_city_portal: { enabled: true, sync_frequency: 'daily', filters: { keywords: [] } },
  // India & Foreign country portals (sync via backend)
  tender_247: { enabled: true, sync_frequency: 'daily', filters: { keywords: [] } },
  tendertiger_india: { enabled: true, sync_frequency: 'daily', filters: { keywords: [] } },
  eprocure: { enabled: true, sync_frequency: 'daily', filters: { keywords: [] } },
  gem: { enabled: true, sync_frequency: 'daily', filters: { keywords: [] } },
  hp_tenders: { enabled: true, sync_frequency: 'daily', filters: { keywords: [] } },
  tender_odisha: { enabled: true, sync_frequency: 'daily', filters: { keywords: [] } },
  uk_tenders: { enabled: true, sync_frequency: 'daily', filters: { keywords: [] } },
  tendertiger_brazil: { enabled: true, sync_frequency: 'daily', filters: { keywords: [] } },
  tendertiger_canada: { enabled: true, sync_frequency: 'daily', filters: { keywords: [] } },
  tendertiger_ethiopia: { enabled: true, sync_frequency: 'daily', filters: { keywords: [] } },
  tendertiger_indonesia: { enabled: true, sync_frequency: 'daily', filters: { keywords: [] } },
  tendertiger_kenya: { enabled: true, sync_frequency: 'daily', filters: { keywords: [] } },
  tendertiger_mexico: { enabled: true, sync_frequency: 'daily', filters: { keywords: [] } },
  tendertiger_peru: { enabled: true, sync_frequency: 'daily', filters: { keywords: [] } },
  tendertiger_south_africa: { enabled: true, sync_frequency: 'daily', filters: { keywords: [] } },
  tendertiger_tanzania: { enabled: true, sync_frequency: 'daily', filters: { keywords: [] } },
  tendertiger_usa: { enabled: true, sync_frequency: 'daily', filters: { keywords: [] } },
};

// ============================================================================
// Registry API
// ============================================================================

export class DataSourceRegistry {
  private static instance: DataSourceRegistry;
  private registry: Map<DataSourceType, DataSourceRegistryEntry>;

  private constructor() {
    this.registry = new Map();
    this.initialize();
  }

  static getInstance(): DataSourceRegistry {
    if (!DataSourceRegistry.instance) {
      DataSourceRegistry.instance = new DataSourceRegistry();
    }
    return DataSourceRegistry.instance;
  }

  private initialize() {
    for (const [id, metadata] of Object.entries(DATA_SOURCE_METADATA)) {
      const sourceId = id as DataSourceType;
      this.registry.set(sourceId, {
        metadata,
        runner: DATA_SOURCE_RUNNERS[sourceId],
        defaultConfig: DEFAULT_CONFIGS[sourceId],
      });
    }
  }

  /**
   * Get all registered data sources
   */
  getAll(): DataSourceRegistryEntry[] {
    return Array.from(this.registry.values());
  }

  /**
   * Get a specific data source by ID
   */
  get(id: DataSourceType): DataSourceRegistryEntry | undefined {
    return this.registry.get(id);
  }

  /**
   * Get all active data sources
   */
  getActive(): DataSourceRegistryEntry[] {
    return this.getAll().filter(entry => entry.metadata.status === 'active');
  }

  /**
   * Get all data sources that require API keys
   */
  getRequiringApiKeys(): DataSourceRegistryEntry[] {
    return this.getAll().filter(entry => entry.metadata.requires_api_key);
  }

  /**
   * Validate a data source configuration
   */
  validate(id: DataSourceType, config: DataSourceConfig): ValidationResult {
    const entry = this.registry.get(id);
    if (!entry) {
      return {
        valid: false,
        errors: [`Unknown data source: ${id}`],
        warnings: [],
      };
    }
    return entry.runner.validate(config);
  }

  /**
   * Test a data source connection
   */
  async test(id: DataSourceType, config: DataSourceConfig): Promise<HealthCheckResult> {
    const entry = this.registry.get(id);
    if (!entry) {
      return {
        healthy: false,
        status: 'error',
        message: `Unknown data source: ${id}`,
        last_checked: new Date().toISOString(),
      };
    }
    return entry.runner.test(config);
  }

  /**
   * Fetch opportunities and awards from a data source
   */
  async fetch(id: DataSourceType, config: DataSourceConfig, projectId: string): Promise<{ opportunities: Opportunity[]; awards: Award[] }> {
    const entry = this.registry.get(id);
    if (!entry) {
      throw new Error(`Unknown data source: ${id}`);
    }

    // Validate configuration first
    const validation = entry.runner.validate(config);
    if (!validation.valid) {
      throw new Error(`Invalid configuration: ${validation.errors.join(', ')}`);
    }

    // Fetch opportunities and awards
    return entry.runner.fetch(config, projectId);
  }

  /**
   * Get default configuration for a data source
   */
  getDefaultConfig(id: DataSourceType): Partial<DataSourceConfig> | undefined {
    return this.registry.get(id)?.defaultConfig;
  }

  /**
   * Get metadata for a data source
   */
  getMetadata(id: DataSourceType): DataSourceMetadata | undefined {
    return this.registry.get(id)?.metadata;
  }
}

// Export singleton instance
export const dataSourceRegistry = DataSourceRegistry.getInstance();

// Export convenience functions
export function getAllDataSources() {
  return dataSourceRegistry.getAll();
}

export function getDataSource(id: DataSourceType) {
  return dataSourceRegistry.get(id);
}

export function validateDataSource(id: DataSourceType, config: DataSourceConfig) {
  return dataSourceRegistry.validate(id, config);
}

export async function testDataSource(id: DataSourceType, config: DataSourceConfig) {
  return dataSourceRegistry.test(id, config);
}

export async function fetchFromDataSource(id: DataSourceType, config: DataSourceConfig, projectId: string): Promise<{ opportunities: Opportunity[]; awards: Award[] }> {
  return dataSourceRegistry.fetch(id, config, projectId);
}

